import { Card, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import { BlueButton } from '../../customComponents/buttons/BlueButton'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { SubmitPlan } from '../../../services/AnnualPlanService'
import { Modal } from '../../customComponents/Modal'

interface Props {
  annualPlanId: number
  rcnId: number
  onSubmit: () => void
  isImplementation?: boolean
}

export const SubmitPlanTab = (props: Props): JSX.Element => {
  const [rcnOperationsChecked, setRcnOperationsChecked] = useState(false)
  const [priorityChecked, setPriorityChecked] = useState(false)
  const [budgetChecked, setBudgetChecked] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const isImplementation = props.isImplementation ?? false

  const submitClicked = async (): Promise<void> => {
    if (!rcnOperationsChecked || !priorityChecked || !budgetChecked) {
      toast.error(`In order to proceed, please confirm that all sections are ${isImplementation ? 'updated' : 'complete'} by checking the boxes.`)
      return
    }

    setIsConfirming(true)
  }

  const confirmSubmit = async (): Promise<void> => {
    await SubmitPlan(props.annualPlanId, props.rcnId, isImplementation)

    toast.success(`Thank you! Your ${isImplementation ? 'Implementation' : 'Annual'} Plan has been submitted to START for review.`)
    setIsConfirming(false)
    props.onSubmit()
  }

  return <Card sx={{ bgcolor: 'var(--grey-100)', p: '24px', borderRadius: '16px' }}>
    <Typography variant='h3'>
      {isImplementation ? 'Submit Implementation Plan Updates' : 'Finalize Annual Plan'}
    </Typography>

    <Stack sx={{ mt: '1em', p: '24px', bgcolor: 'white', border: '1px solid var(--blue-300)', borderRadius: '8px', gap: '32px' }}>
      <Typography variant='h5' sx={{ color: 'var(--blue-700)' }}>
        {isImplementation ? 'Submitting the Implementation Plan Updates' : 'Submitting the Annual Plan'}
      </Typography>

      <Stack sx={{ gap: '10px' }}>
        <Typography variant='body1' sx={{ fontSize: '16px' }}>
          {isImplementation
            ? 'Now that your RCN’s Implementation Plan is updated, you may submit it to START for review. By checking the boxes below, you acknowledge that each of the following sections are updated to the best of your knowledge.'
            : 'Now that your RCN\'s Annual Plan is complete, you may submit it to START for review. Submitting your Annual Plan to START will serve as your RCN\'s application for a contract with START to provide the services and conduct the activities proposed for your region. By checking the boxes below, you acknowledge that each of the following sections are complete and accurate to the best of your knowledge:'}
        </Typography>

        <Stack>
          <FormControlLabel
            label={<Typography variant='h5'>RCN Operations</Typography>}
            sx={{ ml: '12px' }}
            control={<Checkbox checked={rcnOperationsChecked} onChange={() => { setRcnOperationsChecked(!rcnOperationsChecked) }} data-testid='rcnOperationsCheckbox' />}
          />

          <FormControlLabel
            label={<Typography variant='h5'>Priority Activities</Typography>}
            sx={{ ml: '12px' }}
            control={<Checkbox checked={priorityChecked} onChange={() => { setPriorityChecked(!priorityChecked) }} data-testid='prioritiesCheckbox' />}
          />

          <FormControlLabel
            label={<Typography variant='h5'>Budget Overview</Typography>}
            sx={{ ml: '12px' }}
            control={<Checkbox checked={budgetChecked} onChange={() => { setBudgetChecked(!budgetChecked) }} data-testid='budgetCheckbox' />}
          />
        </Stack>
      </Stack>

      <Stack gap='5px'>
        <BlueButton
          onClick={submitClicked}
          dataTestId='submitPlanButton'
          sx={{ width: 'fit-content', py: '12px', px: '24px', fontSize: '16px', fontWeight: '600' }}
        >
          Submit
        </BlueButton>

        {isImplementation && (
          <Typography variant='caption' color='var(--grey-700)'>Complete requirements to submit</Typography>
        )}
      </Stack>
    </Stack>

    {isConfirming &&
      <Modal
        open={true}
        title={`Confirm ${isImplementation ? 'Implementation' : 'Annual'} Plan Submission`}
        confirmButtonText={`Yes, submit the ${isImplementation ? 'Implementation' : 'Annual'} Plan`}
        cancelButtonText={`No, do not submit the ${isImplementation ? 'Implementation' : 'Annual'} Plan`}
        onClose={() => { setIsConfirming(false) }}
        onConfirm={confirmSubmit}
        bodyContent={`Submitting your RCN's ${isImplementation ? 'Implementation' : 'Annual'} Plan will share it with START for review, locking your ability to modify it. Do you wish to proceed?`}
      />
    }
  </Card>
}
